/**
 * React Error Boundary —— 包住可能出错的组件,catch 渲染错误,
 * 显示友好降级 UI 而不是整页白屏。
 *
 * 用法:
 *   <ErrorBoundary fallback={<div>出错了</div>}>
 *     <SomeComponent />
 *   </ErrorBoundary>
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  label?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // 给 console 留下完整 stack 方便排查
    console.error(`[ErrorBoundary${this.props.label ? ' ' + this.props.label : ''}]`, error, info);
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div style={{
          padding: 16,
          background: 'var(--danger-soft)',
          border: '1px solid var(--danger)',
          borderRadius: 8,
          margin: '12px 0',
        }}>
          <div style={{ fontWeight: 600, color: '#b91c1c', marginBottom: 6 }}>
            ⚠️ 这部分内容渲染失败
            {this.props.label && <span style={{ fontWeight: 400, marginLeft: 6 }}>({this.props.label})</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
            {this.state.error?.message || '未知错误'}
          </div>
          <button className="ghost" onClick={this.reset} style={{ fontSize: 12 }}>重试</button>
        </div>
      );
    }
    return this.props.children;
  }
}
